import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Logo from '../../components/common/Logo/Logo';
import { useAuth } from '../../contexts/AuthContext';
import { login as apiLogin, getMyTalentProfile, getMyProducerProfile } from '../../api';
import { resolveProfileImageUrl } from '../../shared/utils/profilePlaceholders';
import { sendPhoneCode, findIdByPhone } from '../../api/authApi';
import FindPasswordModal from '../../shared/settings/FindPasswordModal';
import { markPostLoginNoticePending } from '../../shared/components/PostLoginNoticeModal';
import styles from './LoginPage.module.css';

const inputStyle = {
  boxSizing: 'border-box',
  padding: '0px 18px',
  width: '100%',
  height: '60px',
  background: '#FCFCFD',
  border: '1px solid #E9E9EC',
  borderRadius: '10px',
  fontFamily: 'Pretendard, sans-serif',
  fontWeight: 400,
  fontSize: '18px',
  letterSpacing: '-0.04em',
  color: '#090909',
  outline: 'none',
};

/* ── 아이디 찾기 모달 ── */
function FindIdModal({ onClose }) {
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [phoneStatus, setPhoneStatus] = useState('idle'); // idle | success | error
  const [codeStatus, setCodeStatus] = useState('idle');   // idle | success | error
  const [foundId, setFoundId] = useState('');
  const [isLoadingPhone, setIsLoadingPhone] = useState(false);
  const [isLoadingCode, setIsLoadingCode] = useState(false);
  const [phoneMsg, setPhoneMsg] = useState('');
  const [codeMsg, setCodeMsg] = useState('');

  const PHONE_ERROR_MSG = {
    COOLTIME: '잠시 후 다시 시도해주세요. (재발송 대기 중)',
    DAILY_LIMIT: '오늘 인증번호 발송 횟수를 초과했습니다.',
    INVALID_PHONE_FORMAT: '올바른 휴대폰 번호 형식을 입력해주세요.',
  };

  const CODE_ERROR_MSG = {
    NOT_FOUND: '인증번호 발송 내역이 없습니다. 먼저 인증번호를 요청해주세요.',
    EXPIRED: '인증번호가 만료되었습니다. 다시 요청해주세요.',
    WRONG_CODE: '인증번호가 일치하지 않습니다.',
    LOCKED: '시도 횟수를 초과했습니다. 잠시 후 다시 시도해주세요.',
    ALREADY_USED: '이미 사용된 인증번호입니다. 새로 요청해주세요.',
    NO_ACCOUNT: '인증은 완료되었으나 가입된 계정이 없습니다.',
  };

  async function handleSendCode() {
    if (!phone.trim() || isLoadingPhone) return;
    setIsLoadingPhone(true);
    setPhoneStatus('idle');
    setPhoneMsg('');
    setCodeStatus('idle');
    setCodeMsg('');
    setFoundId('');
    try {
      const res = await sendPhoneCode(phone.trim(), 'FIND_ID');
      if (res?.success === false) {
        setPhoneStatus('error');
        setPhoneMsg(PHONE_ERROR_MSG[res.errorCode] || '인증번호 발송에 실패했습니다.');
      } else {
        setPhoneStatus('success');
        setPhoneMsg('인증번호가 발송되었습니다.');
      }
    } catch {
      setPhoneStatus('error');
      setPhoneMsg('인증번호 발송에 실패했습니다. 번호를 확인해주세요.');
    } finally {
      setIsLoadingPhone(false);
    }
  }

  async function handleVerify() {
    if (!code.trim() || isLoadingCode) return;
    setIsLoadingCode(true);
    setCodeStatus('idle');
    setCodeMsg('');
    setFoundId('');
    try {
      const res = await findIdByPhone(phone.trim(), code.trim());
      if (!res.verified) {
        setCodeStatus('error');
        setCodeMsg(CODE_ERROR_MSG[res.errorCode] || '인증에 실패했습니다.');
      } else if (res.errorCode === 'NO_ACCOUNT') {
        setCodeStatus('error');
        setCodeMsg(CODE_ERROR_MSG.NO_ACCOUNT);
      } else {
        setFoundId(res.email || '');
        setCodeStatus('success');
        setCodeMsg('인증이 완료되었습니다.');
      }
    } catch {
      setCodeStatus('error');
      setCodeMsg('인증 요청 중 오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setIsLoadingCode(false);
    }
  }

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(0,0,0,0.45)',
      }}
      onClick={onClose}
    >
      <div
        style={{
          boxSizing: 'border-box',
          width: '480px',
          background: '#fff',
          borderRadius: '20px',
          padding: '36px 32px',
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* 헤더 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <span style={{ fontFamily: 'Pretendard, sans-serif', fontWeight: 600, fontSize: '24px', letterSpacing: '-0.04em', color: '#090909' }}>
            아이디 찾기
          </span>
          <span style={{ fontFamily: 'Pretendard, sans-serif', fontWeight: 400, fontSize: '15px', letterSpacing: '-0.04em', color: '#A2A2A2' }}>
            가입 시 등록한 휴대폰 번호로 아이디를 찾을 수 있습니다.
          </span>
        </div>

        {/* 휴대폰 번호 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <span style={{ fontFamily: 'Pretendard, sans-serif', fontWeight: 500, fontSize: '16px', letterSpacing: '-0.04em', color: '#090909' }}>
            휴대폰 번호
          </span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <input
              type="tel"
              placeholder="'-' 없이 입력하세요"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              style={{ ...inputStyle, flex: 1, height: '52px', fontSize: '16px' }}
            />
            <button
              onClick={handleSendCode}
              disabled={isLoadingPhone || !phone.trim()}
              style={{
                height: '52px',
                padding: '0 18px',
                borderRadius: '10px',
                border: 'none',
                background: phone.trim() ? '#090909' : '#E9E9EC',
                color: phone.trim() ? '#fff' : '#A2A2A2',
                fontFamily: 'Pretendard, sans-serif',
                fontWeight: 500,
                fontSize: '15px',
                letterSpacing: '-0.04em',
                cursor: phone.trim() ? 'pointer' : 'default',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              {isLoadingPhone ? '발송 중...' : '인증요청'}
            </button>
          </div>
          {phoneMsg && (
            <span style={{ fontFamily: 'Pretendard, sans-serif', fontSize: '13px', color: phoneStatus === 'success' ? '#22B473' : '#FF4343', letterSpacing: '-0.04em' }}>
              {phoneMsg}
            </span>
          )}
        </div>

        {/* 인증번호 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <span style={{ fontFamily: 'Pretendard, sans-serif', fontWeight: 500, fontSize: '16px', letterSpacing: '-0.04em', color: '#090909' }}>
            인증번호
          </span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <input
              type="text"
              placeholder="인증번호 6자리를 입력하세요"
              value={code}
              onChange={e => setCode(e.target.value)}
              style={{ ...inputStyle, flex: 1, height: '52px', fontSize: '16px' }}
            />
            <button
              onClick={handleVerify}
              disabled={isLoadingCode || !code.trim() || phoneStatus !== 'success'}
              style={{
                height: '52px',
                padding: '0 18px',
                borderRadius: '10px',
                border: 'none',
                background: (code.trim() && phoneStatus === 'success') ? '#090909' : '#E9E9EC',
                color: (code.trim() && phoneStatus === 'success') ? '#fff' : '#A2A2A2',
                fontFamily: 'Pretendard, sans-serif',
                fontWeight: 500,
                fontSize: '15px',
                letterSpacing: '-0.04em',
                cursor: (code.trim() && phoneStatus === 'success') ? 'pointer' : 'default',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              {isLoadingCode ? '확인 중...' : '인증확인'}
            </button>
          </div>
          {codeMsg && (
            <span style={{ fontFamily: 'Pretendard, sans-serif', fontSize: '13px', color: codeStatus === 'success' ? '#22B473' : '#FF4343', letterSpacing: '-0.04em' }}>
              {codeMsg}
            </span>
          )}
        </div>

        {/* 아이디 결과 */}
        {foundId && (
          <div style={{
            padding: '16px 20px',
            background: '#F8F8F8',
            borderRadius: '10px',
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
          }}>
            <span style={{ fontFamily: 'Pretendard, sans-serif', fontWeight: 400, fontSize: '14px', color: '#A2A2A2', letterSpacing: '-0.04em' }}>
              등록된 아이디
            </span>
            <span style={{ fontFamily: 'Pretendard, sans-serif', fontWeight: 600, fontSize: '18px', color: '#090909', letterSpacing: '-0.04em' }}>
              {foundId}
            </span>
          </div>
        )}

        {/* 버튼 */}
        <div style={{ display: 'flex', gap: '8px', paddingTop: '4px' }}>
          <button
            onClick={onClose}
            style={{
              flex: 1, height: '52px', borderRadius: '10px',
              border: '1px solid #E9E9EC', background: '#fff',
              fontFamily: 'Pretendard, sans-serif', fontWeight: 500,
              fontSize: '16px', letterSpacing: '-0.04em', color: '#A2A2A2',
              cursor: 'pointer',
            }}
          >
            취소
          </button>
          <button
            onClick={onClose}
            style={{
              flex: 1, height: '52px', borderRadius: '10px',
              border: 'none', background: '#090909',
              fontFamily: 'Pretendard, sans-serif', fontWeight: 600,
              fontSize: '16px', letterSpacing: '-0.04em', color: '#fff',
              cursor: 'pointer',
            }}
          >
            완료
          </button>
        </div>
      </div>
    </div>
  );
}

function LoginPage() {
  const navigate = useNavigate();
  const { isLoggedIn, role, isTalent, isProducer, login, setProfile, setUserInfo } = useAuth();
  const [roleSelectPending, setRoleSelectPending] = useState(null); // { talentProfile, producerProfile }

  // 이미 로그인된 상태로 /login 접근 시 대시보드로 이동
  useEffect(() => {
    if (!isLoggedIn) return;
    if (isTalent && isProducer) {
      setRoleSelectPending({ talentProfile: null, producerProfile: null, profiles: null });
      return;
    }
    if (role) {
      navigate(role === 'TALENT' ? '/talent/dashboard' : '/producer/dashboard', { replace: true });
    }
  }, [isLoggedIn, role, isTalent, isProducer, navigate]);
  const [email, setEmail] = useState(() => localStorage.getItem('saved_email') || '');
  const [password, setPassword] = useState('');
  const [saveEmail, setSaveEmail] = useState(() => !!localStorage.getItem('saved_email'));
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showFindIdModal, setShowFindIdModal] = useState(false);
  const [showFindPasswordModal, setShowFindPasswordModal] = useState(false);

  const isActive = email.trim().length > 0 && password.trim().length > 0;

  const handleLogin = async () => {
    if (!isActive || isLoading) return;
    setIsLoading(true);
    setError('');
    try {
      const data = await apiLogin(email, password);

      // 이메일 저장 / 자동 로그인 처리
      if (saveEmail) {
        localStorage.setItem('saved_email', email);
      } else {
        localStorage.removeItem('saved_email');
      }
      localStorage.removeItem('auto_login');

      const talentFlag = data.isTalent ?? (data.primaryRole === 'TALENT');
      const producerFlag = data.isProducer ?? (data.primaryRole === 'PRODUCER');
      const testFlag = data.isTest ?? false;
      const { profiles } = data;
      const commonUid = data.id ?? data.userId ?? data.memberId ?? null;

      // isTest 계정: DB 역할 무관하게 두 역할 자동 부여, 온보딩 스킵
      if (testFlag) {
        login({
          isTalent: true,
          isProducer: true,
          primaryRole: 'TALENT',
          isTest: true,
          isQnaAdmin: data.isQnaAdmin ?? false,
          userId: commonUid,
        });
        navigate('/talent/dashboard');
        return;
      }

      if (!talentFlag && !producerFlag) {
        setError('이메일 또는 비밀번호를 확인해주세요.');
        return;
      }

      // 두 역할 동시 보유 → 역할 선택 모달
      if (talentFlag && producerFlag) {
        login({
          isTalent: true,
          isProducer: true,
          isQnaAdmin: data.isQnaAdmin ?? false,
          userId: commonUid,
        });
        let talentProfile = null;
        let producerProfile = null;
        try { talentProfile = profiles?.talent?.exists ? await getMyTalentProfile() : null; } catch {}
        try { producerProfile = profiles?.producer?.exists ? await getMyProducerProfile() : null; } catch {}
        setRoleSelectPending({ talentProfile, producerProfile, profiles, isQnaAdmin: data.isQnaAdmin ?? false, userId: data.id ?? data.userId ?? data.memberId ?? null });
        return;
      }

      // 단일 역할
      const primaryRole = talentFlag ? 'TALENT' : 'PRODUCER';
      login({
        isTalent: talentFlag,
        isProducer: producerFlag,
        primaryRole,
        isQnaAdmin: data.isQnaAdmin ?? false,
        userId: commonUid,
      });

      if (talentFlag) {
        if (profiles && !profiles.talent?.exists) {
          navigate('/onboarding/talent');
        } else {
          markPostLoginNoticePending();
          try {
            const talentProfile = await getMyTalentProfile();
            setProfile(talentProfile.id);
            setUserInfo({ name: talentProfile.stageName || talentProfile.name, profileImage: resolveProfileImageUrl(talentProfile) || null, stageName: talentProfile.stageName || null, realName: talentProfile.name || null });
          } catch {}
          navigate('/talent/dashboard');
        }
      } else {
        if (profiles && !profiles.producer?.exists) {
          navigate('/onboarding/producer');
        } else {
          markPostLoginNoticePending();
          try {
            const producerProfile = await getMyProducerProfile();
            setProfile(producerProfile.id);
            setUserInfo({ name: producerProfile.companyName, profileImage: null });
          } catch {}
          navigate('/producer/dashboard');
        }
      }
    } catch {
      setError('이메일 또는 비밀번호를 확인해주세요.');
    } finally {
      setIsLoading(false);
    }
  };

  const btnStyle = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: '60px',
    background: isActive
      ? 'linear-gradient(101.37deg, #FFD92B 0.67%, #FFB517 65.65%)'
      : '#090909',
    borderRadius: '10px',
    border: 'none',
    cursor: 'pointer',
    fontFamily: 'Pretendard, sans-serif',
    fontWeight: 600,
    fontSize: '20px',
    letterSpacing: '-0.04em',
    color: isActive ? '#090909' : '#FFFFFF',
    transition: 'background 0.2s, color 0.2s',
  };

  const handleRoleSelect = async (selectedRole) => {
    const { talentProfile, producerProfile, profiles } = roleSelectPending;
    setRoleSelectPending(null);
    login({
      accessToken: localStorage.getItem('accessToken'),
      refreshToken: localStorage.getItem('refreshToken'),
      isTalent: true,
      isProducer: true,
      primaryRole: selectedRole,
      isQnaAdmin: roleSelectPending.isQnaAdmin ?? false,
      userId: roleSelectPending.userId ?? null,
    });
    if (selectedRole === 'TALENT') {
      if (profiles && !profiles.talent?.exists) {
        navigate('/onboarding/talent');
      } else {
        markPostLoginNoticePending();
        if (talentProfile) {
          setProfile(talentProfile.id);
          setUserInfo({ name: talentProfile.stageName || talentProfile.name, profileImage: resolveProfileImageUrl(talentProfile) || null, stageName: talentProfile.stageName || null, realName: talentProfile.name || null });
        }
        navigate('/talent/dashboard');
      }
    } else {
      if (profiles && !profiles.producer?.exists) {
        navigate('/onboarding/producer');
      } else {
        markPostLoginNoticePending();
        if (producerProfile) {
          setProfile(producerProfile.id);
          setUserInfo({ name: producerProfile.companyName, profileImage: null });
        }
        navigate('/producer/dashboard');
      }
    }
  };

  return (
    <div className={styles.pageWrapper}>
      {roleSelectPending && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: '20px', padding: '40px', width: '360px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
            <div>
              <div style={{ fontSize: '22px', fontWeight: 700, color: '#090909', marginBottom: '8px' }}>어떤 역할로 입장하시겠어요?</div>
              <div style={{ fontSize: '14px', color: '#888896' }}>배우와 제작사 계정을 모두 보유하고 있습니다.</div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <button
                onClick={() => handleRoleSelect('TALENT')}
                style={{ height: '56px', borderRadius: '12px', border: '1.5px solid #090909', background: '#fff', cursor: 'pointer', fontSize: '16px', fontWeight: 600, color: '#090909' }}
              >
                배우로 입장
              </button>
              <button
                onClick={() => handleRoleSelect('PRODUCER')}
                style={{ height: '56px', borderRadius: '12px', border: 'none', background: '#090909', cursor: 'pointer', fontSize: '16px', fontWeight: 600, color: '#fff' }}
              >
                제작사로 입장
              </button>
            </div>
          </div>
        </div>
      )}
      {showFindIdModal && <FindIdModal onClose={() => setShowFindIdModal(false)} />}
      {showFindPasswordModal && <FindPasswordModal onClose={() => setShowFindPasswordModal(false)} />}
      {/* Background */}
      <div className={styles.background} style={{ backgroundImage: 'url(/assets/images/login.jpg)' }} />
      {/* 우측 하단 castar 로고 워터마크 */}
      <img src="/assets/images/login-castar.png" alt="" className={styles.watermark} />

      {/* Modal */}
      <div className={styles.modal}>
        <Logo onClick={() => navigate('/')} style={{ cursor: 'pointer' }} />

        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <span style={{ fontFamily: 'Pretendard, sans-serif', fontWeight: 600, fontSize: '30px', letterSpacing: '-0.04em', color: '#090909' }}>로그인</span>
          <span style={{ fontFamily: 'Pretendard, sans-serif', fontWeight: 400, fontSize: '16px', letterSpacing: '-0.04em', color: '#A2A2A2' }}>Castar에 오신 것을 환영합니다.</span>
        </div>

        {/* Email */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%' }}>
          <span style={{ fontFamily: 'Pretendard, sans-serif', fontWeight: 500, fontSize: '18px', letterSpacing: '-0.04em', color: '#090909' }}>이메일</span>
          <input
            type="email"
            placeholder="이메일을 입력하세요"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={inputStyle}
          />
        </div>

        {/* Password */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%' }}>
          <span style={{ fontFamily: 'Pretendard, sans-serif', fontWeight: 500, fontSize: '18px', letterSpacing: '-0.04em', color: '#090909' }}>비밀번호</span>
          <div style={{ position: 'relative' }}>
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder="비밀번호를 입력하세요"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
              style={{ ...inputStyle, paddingRight: '52px' }}
            />
            <button
              type="button"
              onClick={() => setShowPassword(p => !p)}
              style={{ position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' }}
            >
              {showPassword ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94" stroke="#A2A2A2" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19" stroke="#A2A2A2" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M14.12 14.12a3 3 0 11-4.24-4.24" stroke="#A2A2A2" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M1 1l22 22" stroke="#A2A2A2" strokeWidth="1.8" strokeLinecap="round"/>
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="#A2A2A2" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="12" cy="12" r="3" stroke="#A2A2A2" strokeWidth="1.8"/>
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* 이메일 저장 */}
        <div style={{ display: 'flex', gap: '24px', width: '100%' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontFamily: 'Pretendard, sans-serif', fontWeight: 400, fontSize: '15px', letterSpacing: '-0.04em', color: '#6B6B6B' }}>
            <input type="checkbox" checked={saveEmail} onChange={(e) => setSaveEmail(e.target.checked)} style={{ width: '16px', height: '16px', accentColor: '#090909', cursor: 'pointer' }} />
            이메일 저장
          </label>
        </div>

        {/* Login button + 계정 찾기 */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '12px', width: '100%' }}>
          <button onClick={handleLogin} disabled={isLoading} style={{ ...btnStyle, opacity: isLoading ? 0.7 : 1 }}>
            {isLoading ? '로그인 중...' : '로그인'}
          </button>
          {error && (
            <span style={{ fontFamily: 'Pretendard, sans-serif', fontSize: '14px', color: '#FF4343', letterSpacing: '-0.04em' }}>
              {error}
            </span>
          )}
          <div style={{ display: 'flex', gap: '16px' }}>
            <span
              onClick={() => setShowFindPasswordModal(true)}
              style={{ fontFamily: 'Pretendard, sans-serif', fontWeight: 400, fontSize: '16px', letterSpacing: '-0.04em', color: '#A2A2A2', cursor: 'pointer' }}
            >
              비밀번호 재설정
            </span>
            <span style={{ color: '#E9E9EC' }}>|</span>
            <span
              onClick={() => setShowFindIdModal(true)}
              style={{ fontFamily: 'Pretendard, sans-serif', fontWeight: 400, fontSize: '16px', letterSpacing: '-0.04em', color: '#A2A2A2', cursor: 'pointer' }}
            >
              아이디 찾기
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '100%', paddingTop: '20px', borderTop: '1px solid #EEEEEE' }}>
          <div style={{ display: 'flex', flexDirection: 'row', gap: '6px', width: '100%' }}>
            {/* 카카오
            <button onClick={handleKakaoLogin} style={socialBtnStyle}>
              <svg width="20" height="19" viewBox="0 0 20 19" fill="none">
                <path fillRule="evenodd" clipRule="evenodd" d="M10 0C4.477 0 0 3.358 0 7.5c0 2.658 1.757 4.993 4.394 6.337L3.29 18.02a.375.375 0 0 0 .552.404L8.7 15.19c.425.044.858.068 1.3.068 5.523 0 10-3.358 10-7.5S15.523 0 10 0z" fill="#231916"/>
              </svg>
              카카오 계정으로 로그인
            </button> */}
            {/* 구글 */}
            {/* <button
              onClick={() => {
                if (!googleClientId) {
                  alert('.env에 REACT_APP_GOOGLE_CLIENT_ID를 설정해주세요.');
                  return;
                }
                handleGoogleLogin();
              }}
              style={socialBtnStyle}
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
                <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
                <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
                <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EB4335"/>
              </svg>
              구글 계정으로 로그인
            </button> */}
          </div>

          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', paddingTop: '10px' }}>
            <span style={{ fontFamily: 'Pretendard, sans-serif', fontWeight: 400, fontSize: '16px', letterSpacing: '-0.04em', color: '#A2A2A2' }}>
              아직 Castar 회원이 아니신가요?
            </span>
            <span
              onClick={() => navigate('/signup')}
              style={{ fontFamily: 'Pretendard, sans-serif', fontWeight: 500, fontSize: '16px', letterSpacing: '-0.04em', textDecoration: 'underline', color: '#090909', cursor: 'pointer' }}
            >
              회원가입하기
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
